import { Box, Text, Icon, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { X, Wind, Thermometer, Droplets, Gauge, Sunrise, Sunset, Clock, Calendar } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import { WidgetType } from "../../hooks/useWidgets";
import { ProcessedAirQualityData } from "../../hooks/useAirQuality";

const MotionBox = motion(Box);

const WidgetContainer = styled(MotionBox)<{ $theme: AppTheme }>`
  position: relative;
  background: ${({ $theme }) => $theme.backgroundCard};
  border: 1px solid ${({ $theme }) => $theme.borderCard};
  border-radius: ${THEME.borderRadiusLg};
  padding: 0.75rem;
  box-shadow: ${({ $theme }) => $theme.shadowMd};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%; /* Równe wysokości */
  min-height: 130px; /* Minimalna wysokość */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Zapobiega wychodzeniu treści poza kontener */
  
  &:hover {
    box-shadow: ${({ $theme }) => $theme.shadowLg};
    transform: translateY(-2px);
    border-color: ${({ $theme }) => `${$theme.primary}40`};
  }
`;

const SunTimeContainer = styled(Flex)`
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
`;

const SunTimeItem = styled(Flex)`
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  width: 100%;
`;

const SunTimeValue = styled(Text)`
  font-family: 'Outfit', sans-serif;
  font-size: 0.875rem; /* sm - mniejszy rozmiar dla lepszej czytelności na telefonach */
  font-weight: 700;
  white-space: nowrap;
  
  @media (min-width: 480px) {
    font-size: 0.9375rem; /* nieco większy na większych ekranach */
  }
  
  @media (min-width: 768px) {
    font-size: 1rem; /* md na tabletach i większych */
  }
`;

const ProgressBarContainer = styled(Box)<{ $theme: AppTheme }>`
  width: 100%;
  height: 8px;
  background: ${({ $theme }) => $theme.backgroundMuted};
  border-radius: ${THEME.borderRadiusFull};
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled(MotionBox)<{ $theme: AppTheme; $percentage: number }>`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background: ${({ $theme }) => `linear-gradient(90deg, ${$theme.primary} 0%, ${$theme.brand} 100%)`};
  border-radius: ${THEME.borderRadiusFull};
  transition: width 0.3s ease;
`;

const RemoveButton = styled.button<{ $theme: AppTheme }>`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $theme }) => $theme.backgroundMuted};
  border: 1px solid ${({ $theme }) => $theme.border};
  color: ${({ $theme }) => $theme.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0;
  
  ${WidgetContainer}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background: ${({ $theme }) => $theme.red};
    color: white;
    border-color: ${({ $theme }) => $theme.red};
  }
`;

type WidgetCardProps = {
  widget: { id: string; type: WidgetType };
  airQuality: ProcessedAirQualityData | null;
  onRemove: (id: string) => void;
  isLoading?: boolean;
  sunriseSunset?: { sunrise: string; sunset: string; dayLength: string };
  yearProgress?: { percentage: number };
};

const getWidgetIcon = (type: WidgetType) => {
  switch (type) {
    case "aqi":
    case "pm25":
    case "pm10":
      return Wind;
    case "temperature":
      return Thermometer;
    case "humidity":
      return Droplets;
    case "pressure":
      return Gauge;
    case "sunrise-sunset":
      return Sunrise;
    case "day-length":
      return Clock;
    case "year-progress":
      return Calendar;
    default:
      return Wind;
  }
};

const getWidgetLabel = (type: WidgetType): string => {
  switch (type) {
    case "aqi":
      return "AQI";
    case "pm25":
      return "PM2.5";
    case "pm10":
      return "PM10";
    case "temperature":
      return "Temp.";
    case "humidity":
      return "Wilg.";
    case "pressure":
      return "Ciśn.";
    case "sunrise-sunset":
      return "Słońce";
    case "day-length":
      return "Długość dnia";
    case "year-progress":
      return "Rok";
    default:
      return "";
  }
};

const getWidgetValue = (type: WidgetType, airQuality: ProcessedAirQualityData | null, sunriseSunset?: { sunrise: string; sunset: string; dayLength: string }, yearProgress?: { percentage: number }): string => {
  if (type === "day-length") {
    return sunriseSunset?.dayLength || "--";
  }
  
  if (type === "year-progress") {
    return yearProgress ? `${yearProgress.percentage.toFixed(1)}%` : "--";
  }
  
  if (!airQuality) return "--";
  
  switch (type) {
    case "aqi":
      return airQuality.aqi.toString();
    case "pm25":
      return `${airQuality.pm25} μg/m³`;
    case "pm10":
      return `${airQuality.pm10} μg/m³`;
    case "temperature":
      return `${airQuality.temperature}°C`;
    case "humidity":
      return `${airQuality.humidity}%`;
    case "pressure":
      return `${airQuality.pressure} hPa`;
    default:
      return "--";
  }
};

const getWidgetColor = (type: WidgetType, theme: AppTheme, airQuality: ProcessedAirQualityData | null): string => {
  if (!airQuality && type !== "sunrise-sunset" && type !== "day-length" && type !== "year-progress") return theme.textMuted;
  
  switch (type) {
    case "aqi":
      if (airQuality && airQuality.aqi <= 50) return theme.success;
      if (airQuality && airQuality.aqi <= 100) return theme.warning;
      return theme.red;
    case "pm25":
      return airQuality && airQuality.pm25 > 25 ? theme.warning : theme.success;
    case "pm10":
      return airQuality && airQuality.pm10 > 50 ? theme.warning : theme.success;
    case "temperature":
      return theme.primary;
    case "humidity":
      return theme.brand;
    case "pressure":
      return theme.accent;
    case "sunrise-sunset":
      return theme.accent;
    case "day-length":
      return theme.accent;
    case "year-progress":
      return theme.primary;
    default:
      return theme.text;
  }
};

export function WidgetCard({ widget, airQuality, onRemove, isLoading, sunriseSunset, yearProgress }: WidgetCardProps) {
  const theme = useTheme() as AppTheme;
  const IconComponent = getWidgetIcon(widget.type);
  const label = getWidgetLabel(widget.type);
  const value = getWidgetValue(widget.type, airQuality, sunriseSunset, yearProgress);
  const color = getWidgetColor(widget.type, theme, airQuality);
  const isSunWidget = widget.type === "sunrise-sunset";
  const isYearProgressWidget = widget.type === "year-progress";
  
  // Pokazuj loading tylko jeśli nie mamy danych
  const showLoading = isLoading && !airQuality;

  // Specjalny render dla widgetu słońca
  if (isSunWidget) {
    const sunColor = getWidgetColor(widget.type, theme, null);
    return (
      <WidgetContainer
        $theme={theme}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ 
          opacity: 1, 
          scale: 1,
        }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ y: -2 }}
      >
        <RemoveButton
          $theme={theme}
          onClick={() => onRemove(widget.id)}
          aria-label="Usuń widżet"
        >
          <X size={14} />
        </RemoveButton>
        
        <Flex direction="column" align="center" gap={1.5} flex={1} justify="space-between" minH={0}>
          <MotionBox
            p={1.5}
            borderRadius={THEME.borderRadius}
            bg={`${sunColor}15`}
            color={sunColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={IconComponent} boxSize={4} />
          </MotionBox>
          
          <Text
            fontFamily="'Outfit', sans-serif"
            fontSize="xs"
            fontWeight="600"
            color={theme.textMuted}
            textTransform="uppercase"
            letterSpacing="0.05em"
            flexShrink={0}
            lineHeight="1.2"
            mt="0"
          >
            {label}
          </Text>
          
          <SunTimeContainer flex={1} display="flex" alignItems="center" justifyContent="center" minH={0}>
            <SunTimeItem>
              <Icon as={Sunrise} boxSize={3.5} color={sunColor} />
              <SunTimeValue
                color={sunColor}
              >
                {sunriseSunset?.sunrise || "--:--"}
              </SunTimeValue>
            </SunTimeItem>
            <SunTimeItem>
              <Icon as={Sunset} boxSize={3.5} color={sunColor} />
              <SunTimeValue
                color={sunColor}
              >
                {sunriseSunset?.sunset || "--:--"}
              </SunTimeValue>
            </SunTimeItem>
          </SunTimeContainer>
        </Flex>
      </WidgetContainer>
    );
  }

  // Specjalny render dla widgetu postępu roku
  if (isYearProgressWidget) {
    const progressPercentage = yearProgress?.percentage || 0;
    return (
      <WidgetContainer
        $theme={theme}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ 
          opacity: 1, 
          scale: 1,
        }}
        exit={{ opacity: 0, scale: 0.9 }}
        whileHover={{ y: -2 }}
      >
        <RemoveButton
          $theme={theme}
          onClick={() => onRemove(widget.id)}
          aria-label="Usuń widżet"
        >
          <X size={14} />
        </RemoveButton>
        
        <Flex direction="column" align="center" gap={1.5} flex={1} justify="space-between" minH={0}>
          <MotionBox
            p={1.5}
            borderRadius={THEME.borderRadius}
            bg={`${color}15`}
            color={color}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={IconComponent} boxSize={4} />
          </MotionBox>
          
          <Text
            fontFamily="'Outfit', sans-serif"
            fontSize="xs"
            fontWeight="600"
            color={theme.textMuted}
            textTransform="uppercase"
            letterSpacing="0.05em"
            flexShrink={0}
            lineHeight="1.2"
          >
            {label}
          </Text>
          
          <Flex direction="column" align="center" gap={0.75} flex={1} justify="center" minH={0} width="100%">
            <MotionBox
              as={Text}
              fontFamily="'Outfit', sans-serif"
              fontSize="lg"
              fontWeight="700"
              color={color}
              textAlign="center"
              flexShrink={0}
            >
              {value}
            </MotionBox>
            
            <ProgressBarContainer $theme={theme}>
              <ProgressBarFill
                $theme={theme}
                $percentage={progressPercentage}
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </ProgressBarContainer>
          </Flex>
        </Flex>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer
      $theme={theme}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
      }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -2 }}
    >
      <RemoveButton
        $theme={theme}
        onClick={() => onRemove(widget.id)}
        aria-label="Usuń widżet"
      >
        <X size={14} />
      </RemoveButton>
      
      <Flex direction="column" align="center" gap={1.5} flex={1} justify="space-between" minH={0}>
        <MotionBox
          p={1.5}
          borderRadius={THEME.borderRadius}
          bg={`${color}15`}
          color={color}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon as={IconComponent} boxSize={4} />
        </MotionBox>
        
        <Text
          fontFamily="'Outfit', sans-serif"
          fontSize="xs"
          fontWeight="600"
          color={theme.textMuted}
          textTransform="uppercase"
          letterSpacing="0.05em"
          flexShrink={0}
          lineHeight="1.2"
        >
          {label}
        </Text>
        
        <MotionBox
          as={Text}
          fontFamily="'Outfit', sans-serif"
          fontSize="lg"
          fontWeight="700"
          color={color}
          textAlign="center"
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH={0}
          wordBreak="break-word"
          lineHeight="1.3"
        >
          {showLoading ? "..." : value}
        </MotionBox>
      </Flex>
    </WidgetContainer>
  );
}

